import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { BORDER, TextButton } from './ui'
import { LAYERS } from 'utils/layers'

const MARGIN = 12
const MAX_HEIGHT = 420
const SETTLE_MS = 500
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
const EDITABLE = 'input, textarea, select, [contenteditable="true"]'

const Popover = ({ anchorEl, open, onClose, width = 320, label, id, closeLabel = 'Close', children }) => {
  const popRef = useRef(null)
  const bodyRef = useRef(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const settleUntilRef = useRef(0)
  const [coords, setCoords] = useState(null)

  useLayoutEffect(() => {
    if (!open || !anchorEl) {
      setCoords(null)
      return
    }
    const rect = anchorEl.getBoundingClientRect()
    const w = Math.min(width, window.innerWidth - MARGIN * 2)
    const left = Math.max(MARGIN, Math.min(rect.left, window.innerWidth - w - MARGIN))
    const spaceBelow = window.innerHeight - rect.bottom - MARGIN
    const placeAbove = spaceBelow < 240 && rect.top > spaceBelow
    const room = placeAbove ? rect.top - MARGIN * 2 : spaceBelow - 6
    const maxHeight = room < 160 ? Math.max(0, room) : Math.min(MAX_HEIGHT, room)
    const top = placeAbove ? Math.max(MARGIN, rect.top - 6 - maxHeight) : rect.bottom + 6
    setCoords({ top, left, width: w, maxHeight })
  }, [open, anchorEl, width])

  const shown = open && !!coords

  useEffect(() => {
    if (!shown) return undefined
    settleUntilRef.current = Date.now() + SETTLE_MS
    const pop = popRef.current
    if (pop && !pop.contains(document.activeElement)) {
      const target = bodyRef.current?.querySelector(FOCUSABLE)
      if (target) target.focus({ preventScroll: true })
      else pop.focus({ preventScroll: true })
    }
    return () => {
      const active = document.activeElement
      const focusLost = !active || active === document.body || pop?.contains(active)
      if (focusLost && anchorEl && document.contains(anchorEl)) anchorEl.focus({ preventScroll: true })
    }
  }, [shown, anchorEl])

  useEffect(() => {
    if (!open) return undefined
    const onOutside = e => {
      if (popRef.current?.contains(e.target) || anchorEl?.contains(e.target)) return
      onCloseRef.current?.()
    }
    const onKey = e => {
      if (e.key === 'Escape') onCloseRef.current?.()
    }
    document.addEventListener('mousedown', onOutside)
    document.addEventListener('touchstart', onOutside)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onOutside)
      document.removeEventListener('touchstart', onOutside)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, anchorEl])

  useEffect(() => {
    if (!open) return undefined
    const editingInside = () => {
      const active = document.activeElement
      return !!active && !!popRef.current?.contains(active) && !!active.matches?.(EDITABLE)
    }
    const onScroll = e => {
      if (e?.target instanceof Node && popRef.current?.contains(e.target)) return
      if (editingInside()) return
      onCloseRef.current?.()
    }
    const onResize = () => {
      if (Date.now() < settleUntilRef.current) return
      if (editingInside()) return
      onCloseRef.current?.()
    }
    window.addEventListener('resize', onResize, { capture: true, passive: true })
    window.addEventListener('scroll', onScroll, { capture: true, passive: true })
    return () => {
      window.removeEventListener('resize', onResize, { capture: true, passive: true })
      window.removeEventListener('scroll', onScroll, { capture: true, passive: true })
    }
  }, [open])

  if (!shown) return null

  const onFocusIn = e => {
    if (e.target?.matches?.(EDITABLE)) settleUntilRef.current = Date.now() + SETTLE_MS
  }

  return createPortal(
    <Pop ref={popRef} id={id} role='dialog' aria-label={label} tabIndex={-1} style={coords} onClick={e => e.stopPropagation()} onFocus={onFocusIn}>
      <CloseRow>
        <TextButton type='button' onClick={() => onCloseRef.current?.()} aria-label={closeLabel}>
          Close
        </TextButton>
      </CloseRow>
      <div ref={bodyRef}>{children}</div>
    </Pop>,
    document.body
  )
}

export default Popover

const Pop = styled.div`
  position: fixed;
  z-index: ${LAYERS.popover};
  box-sizing: border-box;
  overflow-y: auto;
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 8px 24px rgba(12, 18, 41, 0.12);
  font-size: 15px;
  &:focus { outline: none; }
`

const CloseRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 4px;
`
